import { FC } from 'react';
import { Button } from '../Button/Button';
import { Icon } from '../Icon/Icon';
import { VisuallyHidden } from '../VisuallyHidden/VisuallyHidden';

interface PauseButtonProps {
	isPaused: boolean;
	onToggle: () => void;
	ariaLabel?: string;
	variant?: 'filled' | 'outline' | 'plain';
	className?: string;
	hideVisually?: boolean;
	style?: React.CSSProperties;
	onFocus?: (e: React.FocusEvent<HTMLButtonElement>) => void;
	onBlur?: (e: React.FocusEvent<HTMLButtonElement>) => void;
	onMouseEnter?: (e: React.MouseEvent<HTMLButtonElement>) => void;
	onMouseLeave?: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

/**
 * Accessible pause/play button for animations and media
 */
export const PauseButton: FC<PauseButtonProps> = ({ isPaused, onToggle, ariaLabel, variant = 'outline', className, hideVisually = false, style, onFocus, onBlur, onMouseEnter, onMouseLeave }) => {
	const defaultLabel = isPaused ? 'Resume animations' : 'Pause animations';

	const buttonContent = (
		<Button
			variant={variant}
			onClick={onToggle}
			aria-label={ariaLabel || defaultLabel}
			data-animation-control="true"
			className={className}
			style={style}
			onFocus={onFocus}
			onBlur={onBlur}
			onMouseEnter={onMouseEnter}
			onMouseLeave={onMouseLeave}
		>
			<Icon>
				{isPaused ? (
					<svg
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
					>
						<polygon points="5,3 19,12 5,21" />
					</svg>
				) : (
					<svg
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
					>
						<rect
							x="6"
							y="4"
							width="4"
							height="16"
						/>
						<rect
							x="14"
							y="4"
							width="4"
							height="16"
						/>
					</svg>
				)}
			</Icon>
			<VisuallyHidden>{ariaLabel || defaultLabel}</VisuallyHidden>
		</Button>
	);

	if (hideVisually) {
		return <VisuallyHidden>{buttonContent}</VisuallyHidden>;
	}

	return buttonContent;
};
